import { useState } from 'react'

import ErrorView from '@components/ErrorView'
import LoadingIndicator from '@components/LoadingIndicator'
import useServices from '@hooks/useServices'
import { ChurchService } from '@interfaces/service.interface'
import LibraryFilters, { useLibraryScope } from '../LibraryFilters'
import ServiceList, { filterServices } from '../ServiceList'

import servicesIcon from 'url:@assets/icons/newspaper.svg';

// Long enough that a quick load never shows the spinner at all.
const SPINNER_DELAY_MS = 250;

// The All Services tab: every service for a campus and category, a page at a
// time.
export default function ServicesLibraryPage() {
  const { campus, category, hasCampuses, isReady, select } = useLibraryScope();
  const [query, setQuery] = useState('');

  const {
    services,
    loading,
    hasLoaded,
    error,
    hasMore,
    isLoadingMore,
    loadMore,
    refetch
  } = useServices(campus?.id ?? null, category?.id ?? null);

  return <div className='services-page'>
    <LibraryFilters
      campus={campus}
      category={category}
      select={select}
      query={query}
      onQueryChange={setQuery}
      searchPlaceholder='Search by title or description' />

    <ServicesResults
      isReady={isReady}
      hasCampuses={hasCampuses}
      campusName={campus?.attributes.name ?? null}
      categoryName={category?.name ?? null}
      services={services}
      query={query}
      loading={loading}
      hasLoaded={hasLoaded}
      error={error}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      loadMore={loadMore}
      refetch={() => {
        void refetch();
      }} />
  </div>
}

function ServicesResults(props: {
  isReady: boolean;
  hasCampuses: boolean;
  campusName: string | null;
  categoryName: string | null;
  services: ChurchService[];
  query: string;
  loading: boolean;
  hasLoaded: boolean;
  error: string | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}) {
  // Until the church loads, no campuses means "not yet", not "none".
  if (!props.isReady) {
    return <div className='services-state'>
      <LoadingIndicator fullScreen={false} delayMs={SPINNER_DELAY_MS} />
    </div>
  }

  if (!props.hasCampuses) {
    return <ErrorView
      icon={servicesIcon}
      title='No Campuses Yet'
      error='Services belong to a campus. Add a campus in Settings to get started.'
      showButton={false}
      fullHeight />
  }

  if (!props.campusName) {
    return <ErrorView
      icon={servicesIcon}
      title='Choose a Campus'
      error="Pick a campus above to see its services. You can narrow them to a category too."
      showButton={false}
      fullHeight />
  }

  if (props.loading && !props.hasLoaded) {
    return <div className='services-state'>
      <LoadingIndicator fullScreen={false} delayMs={SPINNER_DELAY_MS} />
    </div>
  }

  // A failed Load More keeps what's already listed; only a failed first page
  // takes over.
  if (props.error && props.services.length === 0) {
    return <ErrorView
      error={props.error}
      buttonText='Retry'
      buttonAction={props.refetch}
      fullHeight />
  }

  if (props.services.length === 0) {
    return <ErrorView
      icon={servicesIcon}
      title='No Services'
      error={props.categoryName ?
        `${props.campusName} doesn't have any services in ${props.categoryName} yet.` :
        `${props.campusName} doesn't have any services without a category yet.`}
      showButton={false}
      fullHeight />
  }

  const isSearching = props.query.trim().length > 0;
  const matches = filterServices(props.services, props.query);

  // Search only covers the pages loaded so far, so while there are more it
  // says so, and offers to look further.
  if (matches.length === 0) {
    return <ErrorView
      icon={servicesIcon}
      title='No Matches'
      error={props.hasMore ?
        `None of the ${props.services.length} services loaded so far match "${props.query.trim()}". Older services haven't been searched yet.` :
        `No services match "${props.query.trim()}".`}
      showButton={props.hasMore}
      buttonText={props.isLoadingMore ? 'Loading…' : 'Search Older Services'}
      buttonAction={props.loadMore}
      fullHeight />
  }

  return <>
    {isSearching && props.hasMore ? <p className='services-search-note'>
      Showing matches from the {props.services.length} most recent services. Load more to search older ones.
    </p> : null}

    <ServiceList services={matches} />

    {props.error ? <p className='services-error'>{props.error}</p> : null}

    {props.hasMore ? <div className='services-load-more'>
      <button type='button' disabled={props.isLoadingMore} onClick={props.loadMore}>
        {props.isLoadingMore ? 'Loading…' : 'Load More'}
      </button>
    </div> : null}
  </>
}
